import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { animated } from '@react-spring/web';

import type { TTextBoxContentItem, TTextContentItem } from '@src/types/wysiwyg_editor';
import { EditorState, TextSelection, Plugin } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { undo, redo, history } from 'prosemirror-history';
import { keymap } from 'prosemirror-keymap';
import { baseKeymap } from 'prosemirror-commands';
import TextSchema, { defaultMarkValues } from '@src/prose_mirror/schemas/TextSchema';
import { DOMParser, DOMSerializer } from 'prosemirror-model';
import { PositionableTextDocument } from '@src/lib/PositionableTextDocument';
import { useDispatch } from 'react-redux';
import { saveSnapshot, updateTextContentItemContent } from '../../actions/wysiwyg/editor';
import { handleError } from '@src/utils/error';
import TextOverflowIndicator from '@src/components/stencils/TextOverflowIndicator';
import useDebouncedCallback from '@src/hooks/useDebouncedCallback';
import type { SprungRect } from '@src/types/misc';
import { TextEditorDispatchContext } from '@src/contexts/wysiwyg/text_editor';
import { applyMarksToPastedContent, markHandling } from '@src/prose_mirror/plugins/marks';
import { limitSelectionToContentNodes } from '@src/prose_mirror/plugins/selection';
import { cleanWhitespace, escapeHtml } from '@src/utils/html';
import { WysiwygEditorStateContext } from '@src/contexts/wysiwyg/editor';
import { isTextBox } from '@src/utils/wysiwyg_editor/data';

type TProps = {
  disableAutosave?: boolean;
  defaultAlignment: string;
  initialContent: PositionableTextDocument;
  focusOnRender?: boolean;
  location: SprungRect;
  textItem: TTextContentItem;
  localDefaultMarkValues: Record<string, string | number>;
  isSingleLine: boolean;
  overflowIndicatorPosition: {
    top?: number;
    left?: number;
    right?: number;
  };
  textOverflows: boolean;
  zIndex?: number;
  onViewMount?: (view: EditorView) => void;
};

function calculateScale(positionEl, scaleBasisDimensions): number {
  const elRect = positionEl.getBoundingClientRect();

  return elRect.width / scaleBasisDimensions.width;
}

function TextEditor(props: TProps): React.ReactElement {
  const { setActiveView, updateSelectionData } = useContext(TextEditorDispatchContext);

  const editorNodeRef = useRef<HTMLDivElement | null>(null);
  const editorViewRef = useRef<EditorView | null>(null);
  const positionElRef = useRef<HTMLDivElement | null>(null);
  const shouldSaveUndoSnapshot = useRef(false);

  const dispatch = useDispatch();
  const { domScaleBasisDimensions, offsetParentRef } = useContext(WysiwygEditorStateContext);
  const [renderScale] = useState(calculateScale(offsetParentRef.current, domScaleBasisDimensions));
  const [appliedDefaultMarkValues] = useState({
    ...defaultMarkValues,
    ...props.localDefaultMarkValues
  });

  const saveEditorState = useCallback(
    (id: string, view: EditorView) => {
      // if there is no parent, the element is not mounted
      if (!view.dom.parentElement) return;

      // if the body doesn't contain the element, it is detached and we can't
      // use it
      if (!document.body?.contains(view.dom)) return;

      if (props.disableAutosave) return;

      const action = updateTextContentItemContent(
        id,
        PositionableTextDocument.fromProseMirrorEditorView(view).toPositionedDocument()
      );

      if (shouldSaveUndoSnapshot.current) {
        shouldSaveUndoSnapshot.current = false;
        dispatch(saveSnapshot());
        dispatch(action);
      } else {
        dispatch(action);
      }
    },
    [dispatch, props.disableAutosave]
  );

  const debouncedSaveEditorState = useDebouncedCallback(saveEditorState, [saveEditorState], 300);

  const [schema] = useState(new TextSchema(renderScale, !props.isSingleLine, props.defaultAlignment));

  const [editorState] = useState<EditorState>((): EditorState => {
    const el = document.createElement('div');
    el.innerHTML = props.initialContent.toProseDocumentHTML();

    const doc = DOMParser.fromSchema(schema).parse(el, {
      preserveWhitespace: true
    });

    return EditorState.create({
      doc,
      schema,
      selection: TextSelection.atEnd(doc),
      plugins: [
        history(),
        keymap({ 'Mod-z': undo, 'Shift-Mod-z': redo }),
        keymap(baseKeymap),
        limitSelectionToContentNodes(),
        markHandling(),
        applyMarksToPastedContent(),
        new Plugin({
          view(view) {
            return {
              update(view: EditorView, prevState: EditorState) {
                if (!view.state.doc.eq(prevState.doc)) {
                  shouldSaveUndoSnapshot.current = true;
                }
                debouncedSaveEditorState(props.textItem.id, view);
                updateSelectionData(view);
              },
              destroy() {
                saveEditorState(props.textItem.id, view);
              }
            };
          }
        })
      ]
    });
  });

  useLayoutEffect(() => {
    const view = new EditorView(editorNodeRef.current, {
      state: editorState,
      dispatchTransaction(transaction) {
        try {
          const newState = view.state.apply(transaction);
          view.updateState(newState);
        } catch (error) {
          alert('Sorry, your last action failed. Please try again.');
          handleError(error);
        }
      },
      clipboardSerializer: DOMSerializer.fromSchema(
        new TextSchema(1, editorState.schema.newlyMeta.allowMultipleLines, editorState.schema.defaultAlignment)
      ),
      transformPastedHTML(html) {
        try {
          // Strip HTML from user-pasted content
          const tempEl = document.createElement('div');
          tempEl.innerHTML = cleanWhitespace(escapeHtml(html));

          return tempEl.textContent || tempEl.innerText || '';
        } catch (error) {
          handleError(error, `Error raised while attempting to paste content: ${html}`);

          alert('There was a problem processing the pasted content, we have been notified about the issue.');

          return '';
        }
      },
      transformPastedText(text) {
        try {
          return cleanWhitespace(text);
        } catch (e) {
          return '';
        }
      }
    });
    editorViewRef.current = view;

    return () => {
      setActiveView(null);
      view.destroy();
    };
  }, [editorState, setActiveView]);

  useEffect(() => {
    const onViewMount = props.onViewMount;

    onViewMount?.(editorViewRef.current);
  }, [props.onViewMount]);

  useEffect(() => {
    if (props.focusOnRender) editorViewRef.current?.focus();
  }, [props.focusOnRender]);

  return (
    <div>
      <animated.div
        ref={positionElRef}
        style={{
          display: 'flex',
          whiteSpace: 'nowrap',
          wordWrap: 'normal',
          position: 'absolute',
          outline: 'none',
          height: props.location.height,
          width: props.location.width,
          top: props.location.y,
          left: props.location.x
        }}
        className="prosemirror-position"
      >
        <div
          ref={editorNodeRef}
          className="prosemirror-editor"
          touch-action="none"
          style={{
            zIndex: props.zIndex,
            backgroundColor: isTextBox ? (props.textItem as TTextBoxContentItem).backgroundColor : null
          }}
        />
        <TextOverflowIndicator position={props.overflowIndicatorPosition} show={props.textOverflows} />
      </animated.div>
    </div>
  );
}

export default TextEditor;
